import React, { useCallback, useMemo } from 'react';
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { useFeatureTour } from '@/contexts/FeatureTourContext';
import { getInteractiveTourSteps } from '@/types/feature-tour';
import { useAppTranslations } from '@/hooks/useAppTranslations';

export function InteractiveTour() {
  const {
    run,
    stepIndex,
    setStepIndex,
    stopTour,
    completeTour,
    completeChecklistItem,
  } = useFeatureTour();
  const { t } = useAppTranslations();
  const steps = useMemo(() => getInteractiveTourSteps(t), [t]);

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, type, index, action, lifecycle } = data;

    console.log('🎯 Tour callback:', {
      status,
      type,
      index,
      action,
      lifecycle,
      totalSteps: steps.length,
      currentStep: steps[index]?.title
    });

    // Handle tour completion - only if we're at the last step
    if ((status === STATUS.FINISHED || status === STATUS.SKIPPED) && index >= steps.length - 1) {
      console.log('✅ Tour completing at final step...');
      completeTour();
      return;
    }

    // Prevent premature completion
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      console.warn('⚠️ Tour tried to complete early at step', index, '- preventing');
      return;
    }

    // Handle step changes - ONLY on STEP_AFTER (user clicked Next/Back)
    if (type === EVENTS.STEP_AFTER) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      console.log(`➡️ Advancing from step ${index} to ${nextStepIndex}`);

      // Check if current step has a checklist item to complete
      const currentStep = steps[index];
      if (currentStep?.checklistKey) {
        console.log(`✓ Completing checklist item: ${currentStep.checklistKey}`);
        completeChecklistItem(currentStep.checklistKey);
      }

      // Advance to next step
      setStepIndex(nextStepIndex);
    }

    // When target not found, just wait and log it
    if (type === EVENTS.TARGET_NOT_FOUND) {
      console.warn(`⚠️ Step ${index} target not found: ${steps[index]?.title} - waiting...`);
      // Don't advance - stay on this step until element appears or user clicks Next
    }

    // Handle close/skip
    if (action === ACTIONS.CLOSE || action === ACTIONS.SKIP) {
      console.log('❌ Tour stopped by user action:', action);
      stopTour();
    }
  }, [steps, setStepIndex, stopTour, completeTour, completeChecklistItem]);

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      scrollToFirstStep
      scrollOffset={100}
      disableScrolling={false}
      disableOverlayClose
      spotlightClicks
      disableCloseOnEsc
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: '#52B788',
          textColor: '#1B4332',
          backgroundColor: '#ffffff',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          arrowColor: '#ffffff',
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 16,
          padding: 20,
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        tooltipTitle: {
          fontSize: '1.25rem',
          fontWeight: 600,
          marginBottom: 8,
        },
        tooltipContent: {
          fontSize: '0.9375rem',
          lineHeight: 1.6,
          padding: '10px 0',
        },
        buttonNext: {
          backgroundColor: '#52B788',
          borderRadius: 8,
          padding: '10px 20px',
          fontSize: '0.875rem',
          fontWeight: 600,
        },
        buttonBack: {
          color: '#1B4332',
          marginRight: 10,
        },
        buttonSkip: {
          color: '#9CA3AF',
        },
        buttonClose: {
          display: 'none', // Hide default close button
        },
        beacon: {
          inner: '#52B788',
          outer: 'rgba(82, 183, 136, 0.3)',
        },
      }}
      locale={{
        back: t.tour.back,
        close: t.tour.close,
        last: t.tour.last,
        next: t.tour.next,
        open: t.tour.open,
        skip: t.tour.skip,
      }}
      floaterProps={{
        disableAnimation: false,
        styles: {
          arrow: {
            length: 8,
            spread: 16,
          },
        },
      }}
    />
  );
}
